import { useCallback, useMemo } from "react";
import type { FilterConfig } from "@/lib/types/data.types";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface StatFilterPreset<TFilter extends FilterConfig = FilterConfig> {
  /** Unique key matching the stat card's identifier. */
  key: string;
  /**
   * Predicate applied to the table when the card is activated.
   *
   * @remarks
   * Accepts three shapes:
   * - `null` — clears all active filters (use for an "All" card).
   * - A single `TFilter` — applied as the sole active filter.
   * - A `TFilter[]` — a compound filter; entries are ANDed together
   *   by the backend (e.g. a numeric range `[GTE x, LTE y]`).
   *
   * @example
   * ```ts
   * // Range card matching vehicle counts between 1 and 5 inclusive.
   * {
   *   filter: [
   *     { column: "vehicle_count", operator: "GTE", value: 1 },
   *     { column: "vehicle_count", operator: "LTE", value: 5 },
   *   ],
   * }
   * ```
   */
  filter: TFilter | TFilter[] | null;
}

export interface UseStatCardFiltersConfig<
  TFilter extends FilterConfig = FilterConfig,
> {
  /** Current active filters (typically from URL search params). */
  filters: TFilter[];
  /** Preset definitions mapping stat card keys to filter rules. */
  presets: StatFilterPreset<TFilter>[];
  /** Callback to update the filter state (typically writes to URL). */
  onFiltersChange: (filters: TFilter[]) => void;
  /**
   * When `true`, clicking a card preserves any unrelated active filters and
   * only swaps the dimension the cards collectively own (the union of every
   * preset's filter columns — e.g. `status`). Activating a card replaces just
   * that dimension; the "All"/null card and toggling-off clear only that
   * dimension. Card-active detection becomes a subset match so a card stays
   * highlighted even while other filter chips are applied.
   *
   * When `false` (default), clicking a card replaces the entire filter set with
   * the preset (or clears it) — the original behavior.
   */
  preserveOtherFilters?: boolean;
}

export interface UseStatCardFiltersReturn {
  /** Key of the currently active preset, or null if no preset matches. */
  activePresetKey: string | null;
  /** Click handler — pass the preset key. Handles toggle and clear logic. */
  onStatCardClick: (key: string) => void;
}

// ─── Pure helpers ────────────────────────────────────────────────────────────

function filtersMatch(a: FilterConfig, b: FilterConfig): boolean {
  // Stringify values: DataFilter emits strings; presets may use numbers.
  return (
    a.column === b.column &&
    a.operator === b.operator &&
    String(a.value) === String(b.value)
  );
}

function presetFilterMatchesActive<TFilter extends FilterConfig>(
  presetFilter: TFilter | TFilter[],
  activeFilters: TFilter[],
): boolean {
  const presetList = Array.isArray(presetFilter) ? presetFilter : [presetFilter];
  if (presetList.length !== activeFilters.length) return false;
  // Equal-length one-way `every`+`some` is order-insensitive but only sound
  // because a well-formed preset never contains duplicate entries.
  return presetList.every(function presetEntryHasMatch(presetEntry) {
    return activeFilters.some(function activeEntryEquals(activeEntry) {
      return filtersMatch(presetEntry, activeEntry);
    });
  });
}

/**
 * Preserve-mode match: the preset is active when all of its entries are present
 * in the active filters, regardless of any unrelated filters also applied.
 */
function presetFilterSubsetOfActive<TFilter extends FilterConfig>(
  presetFilter: TFilter | TFilter[],
  activeFilters: TFilter[],
): boolean {
  const presetList = Array.isArray(presetFilter) ? presetFilter : [presetFilter];
  return presetList.every(function presetEntryHasMatch(presetEntry) {
    return activeFilters.some(function activeEntryEquals(activeEntry) {
      return filtersMatch(presetEntry, activeEntry);
    });
  });
}

/** Columns the cards collectively own — the union of every preset's filter columns. */
function ownedColumns<TFilter extends FilterConfig>(
  presets: StatFilterPreset<TFilter>[],
): Set<string> {
  const columns = new Set<string>();
  presets.forEach(function collect(preset) {
    if (preset.filter === null) return;
    const list = Array.isArray(preset.filter) ? preset.filter : [preset.filter];
    list.forEach(function add(entry) {
      columns.add(entry.column);
    });
  });
  return columns;
}

/**
 * Determines which preset matches the current filter state.
 *
 * - Empty filters → matches the preset with `filter: null` (the "clear all" card)
 * - One filter → matches a preset whose single filter equals it by column + operator + value
 * - Multiple filters → matches a preset whose filter array is the same set of entries
 *   (used by compound presets like a numeric range expressed as `[GTE x, LTE y]`)
 * - No match → null (manual filters leave no card selected)
 */
export function getActivePresetKey<TFilter extends FilterConfig>(
  filters: TFilter[],
  presets: StatFilterPreset<TFilter>[],
  preserveOtherFilters = false,
): string | null {
  if (preserveOtherFilters) {
    // A card is active when its entries are present, ignoring unrelated chips.
    const matched = presets.find(function presetMatches(preset) {
      if (preset.filter === null) return false;
      return presetFilterSubsetOfActive(preset.filter, filters);
    });
    if (matched) return matched.key;
    // No owned-dimension filter present → the null/"All" card is active.
    return presets.find((p) => p.filter === null)?.key ?? null;
  }

  if (filters.length === 0) {
    return presets.find((p) => p.filter === null)?.key ?? null;
  }

  const matchedPreset = presets.find(function presetMatches(preset) {
    if (preset.filter === null) return false;
    return presetFilterMatchesActive(preset.filter, filters);
  });

  return matchedPreset?.key ?? null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Manages stat card filter preset activation and toggle logic.
 *
 * Designed for stat cards that act as clickable filter presets (like chip
 * filters). Reusable across verticals — each vertical defines its own
 * presets array, the hook handles active detection and click behavior.
 */
export function useStatCardFilters<TFilter extends FilterConfig = FilterConfig>(
  config: UseStatCardFiltersConfig<TFilter>,
): UseStatCardFiltersReturn {
  const { filters, presets, onFiltersChange, preserveOtherFilters = false } = config;

  const activePresetKey = useMemo(
    () => getActivePresetKey(filters, presets, preserveOtherFilters),
    [filters, presets, preserveOtherFilters],
  );

  const onStatCardClick = useCallback(
    (key: string) => {
      const preset = presets.find((p) => p.key === key);
      if (!preset) return;

      if (preserveOtherFilters) {
        // Strip only the dimension the cards own (e.g. status), keeping every
        // unrelated filter chip intact.
        const owned = ownedColumns(presets);
        const otherFilters = filters.filter((f) => !owned.has(f.column));

        // Clicking the active card, or the null/"All" card, clears just the
        // owned dimension and leaves the rest of the query untouched.
        if (activePresetKey === key || preset.filter === null) {
          onFiltersChange(otherFilters);
          return;
        }

        const presetList = Array.isArray(preset.filter)
          ? preset.filter
          : [preset.filter];
        onFiltersChange([...otherFilters, ...presetList]);
        return;
      }

      // Clicking the already-active preset deactivates it (clears filters)
      if (activePresetKey === key) {
        onFiltersChange([]);
        return;
      }

      // A null-filter preset always clears (e.g. "Total Submissions")
      if (preset.filter === null) {
        onFiltersChange([]);
        return;
      }

      const nextFilters = Array.isArray(preset.filter)
        ? preset.filter
        : [preset.filter];
      onFiltersChange(nextFilters);
    },
    [presets, activePresetKey, onFiltersChange, preserveOtherFilters, filters],
  );

  return { activePresetKey, onStatCardClick };
}
